'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecommendationsFragmentFragmentDoc } from '@packages/gql/generated/shopping/RecommendationsFragmentFragmentDoc';
import { CriteoProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/CriteoProductsRecoSlider/CriteoProductsRecoSlider';
import { SeriesProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/SeriesProductsRecoSlider/SeriesProductsRecoSlider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import { fallbackRDEHeadlines } from './messages';

/* GraphQL */ `
  fragment RecommendationsFragment on Product {
    hasSeries
    promotion
    orderNumber
    brand {
      name
    }
    availability {
      state
    }
    price {
      value
    }
  }
`;

export type RecommendationsProps = {
  recosData: FragmentType<typeof RecommendationsFragmentFragmentDoc>;
};

/**
 * The `Recommendation` component is used as a slot, which bundles different recommendation sliders.
 *
 * As of now the sliders show recommendations for:
 * <ul>
 *  <li>"Mehr aus dieser Serie - "</li>
 *  <li>"Gesponsorte Artikel - Criteo"</li>
 * </ul>
 *
 * This component is used as part of the detailview-grid.
 * */
export const Recommendations = ({ recosData }: RecommendationsProps) => {
  const { orderNumber, brand, hasSeries, promotion, availability, price } = getFragmentData(
    RecommendationsFragmentFragmentDoc,
    recosData,
  );

  const { formatMessage } = useIntl();
  const {
    product: {
      recommendations: {
        dv: {
          slots: { '3': recoSlot3 },
        },
      },
    },
  } = useConfig();

  return (
    <>
      {hasSeries && (
        <SeriesProductsRecoSlider
          productId={`${orderNumber}${promotion}`}
          elementIndex={1}
          fetchOnThreshold
          pageTemplate="detailview"
          title={formatMessage(fallbackRDEHeadlines.seriesFallback)}
          key={`${orderNumber}${1}`}
        />
      )}
      {recoSlot3 === 'criteoProducts' && (
        <CriteoProductsRecoSlider
          available={availability.state === 'NOT_AVAILABLE' ? 0 : 1}
          price={parseFloat(price.value.toFixed(2))}
          productId={orderNumber}
          fetchOnThreshold
          elementIndex={hasSeries ? 2 : 1}
          pageTemplate="detailview"
          title={formatMessage(fallbackRDEHeadlines.criteoFallback)}
          key={`${orderNumber}${2}`}
          brand={brand?.name ?? ''}
        />
      )}
    </>
  );
};
